<script setup lang="ts">
import { EmptyState, TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'

import { listWarehouses, type Warehouse } from '../api/warehouses'
import {
  STATUS_LABELS,
  STATUS_TAG,
  cancelWave,
  listWaves,
  type ListRow,
  type WaveStatus,
} from '../api/waves'

const router = useRouter()

const rows = ref<ListRow[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref<unknown>(null)
const notice = ref('')

const warehouses = ref<Warehouse[]>([])
const refError = ref<unknown>(null)

// 参照下拉用 undefined 表示「未选」：0 会被 el-select 当成值显示成「0」，placeholder 就没了
const query = reactive({
  keyword: '',
  warehouse: undefined as number | undefined,
  status: '',
  page: 1,
  size: 20,
})

function label(id: number, list: { id: number; code: string; name: string }[]): string {
  const hit = list.find((item) => item.id === id)
  return hit ? `${hit.code} ${hit.name}`.trim() : `#${id}`
}

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listWaves({
      page: query.page,
      size: query.size,
      keyword: query.keyword.trim() || undefined,
      warehouse: query.warehouse || undefined,
      status: query.status || undefined,
    })
    rows.value = res.list
    total.value = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    rows.value = []
  } finally {
    loading.value = false
  }
}

async function loadRefs(): Promise<void> {
  refError.value = null
  try {
    warehouses.value = (await listWarehouses({ page: 1, size: 100 })).list
  } catch (e) {
    // 参照数据拿不到下拉会空，但必须把原因露出来（如未选货主时后端回 422），否则页面看着像没数据
    refError.value = e
  }
}

onMounted(() => {
  void load()
  void loadRefs()
})

function search(): void {
  query.page = 1
  void load()
}

function page(p: number): void {
  query.page = p
  void load()
}

async function remove(row: ListRow): Promise<void> {
  notice.value = ''
  error.value = null
  try {
    await cancelWave(row.id)
    await load(`波次「${row.doc_no}」已取消，库存锁定已释放`)
  } catch (e) {
    error.value = e
  }
}

function open(row: ListRow): void {
  void router.push({ name: 'wave-detail', params: { id: row.id } })
}

const canCancel = (status: WaveStatus) => status === 'open' || status === 'picking'
</script>

<template>
  <div class="yc-wave">
    <header class="yc-wave__bar">
      <el-input
        v-model="query.keyword"
        class="yc-wave__keyword"
        placeholder="波次单号"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select v-model="query.warehouse" class="yc-wave__ref" placeholder="全部仓库" clearable @change="search">
        <el-option v-for="w in warehouses" :key="w.id" :label="label(w.id, warehouses)" :value="w.id" />
      </el-select>
      <el-select v-model="query.status" class="yc-wave__status" placeholder="全部状态" clearable @change="search">
        <el-option v-for="(text, value) in STATUS_LABELS" :key="value" :label="text" :value="value" />
      </el-select>
      <el-button @click="search">查询</el-button>
      <el-button @click="load()">刷新</el-button>
      <span v-if="notice" class="yc-wave__notice">{{ notice }}</span>
      <span class="yc-wave__hint">波次在出库单详情里生成：选已释放的出库单 → 生成波次。</span>
    </header>

    <div v-if="error || refError" class="yc-wave__error">
      <TraceIdAlert :error="error ?? refError" />
    </div>

    <el-card v-loading="loading" class="yc-wave__card">
      <el-table v-if="rows.length" :data="rows" row-key="id">
        <el-table-column prop="doc_no" label="波次单号" width="180" />
        <el-table-column label="仓库" min-width="150">
          <template #default="{ row }">{{ row.warehouse_name || `#${row.warehouse_id}` }}</template>
        </el-table-column>
        <el-table-column label="状态" width="96">
          <template #default="{ row }">
            <el-tag :type="STATUS_TAG[row.status as WaveStatus]" size="small">
              {{ STATUS_LABELS[row.status as WaveStatus] ?? row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="拣货进度" min-width="170">
          <template #default="{ row }">
            <el-progress
              :percentage="row.task_count ? Math.round((row.done_count / row.task_count) * 100) : 0"
              :stroke-width="8"
              :status="row.task_count && row.done_count >= row.task_count ? 'success' : undefined"
            />
            <span class="yc-wave__qty">{{ row.done_count }} / {{ row.task_count }} 条任务</span>
          </template>
        </el-table-column>
        <el-table-column prop="order_count" label="出库单数" width="100" />
        <el-table-column prop="created_by" label="创建人" width="110" />
        <el-table-column label="创建时间" min-width="155">
          <template #default="{ row }">{{ formatDateTime(row.created_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="150" fixed="right">
          <template #default="{ row }">
            <el-button link type="primary" @click="open(row)">详情</el-button>
            <el-popconfirm
              v-if="canCancel(row.status)"
              title="确认取消该波次？库存锁定会释放，出库单退回已释放。"
              width="300"
              @confirm="remove(row)"
            >
              <template #reference>
                <el-button v-perm="'wms:wave:cancel'" link type="danger">取消</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <EmptyState
        v-else-if="!loading"
        title="还没有波次"
        description="在出库单详情里释放后点「生成波次」，系统会按效期/入库时间分配拣货任务。"
      />
    </el-card>

    <el-pagination
      v-if="total > query.size"
      class="yc-wave__page"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="query.size"
      :current-page="query.page"
      @current-change="page"
    />
  </div>
</template>

<style scoped lang="scss">
.yc-wave {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 180px;
  }

  &__ref {
    width: 190px;
  }

  &__status {
    width: 140px;
  }

  &__notice {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__hint {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__qty {
    display: block;
    margin-top: var(--yc-space-1);
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }
}
</style>
